import { useEffect, useId } from 'react';
import FormField, { FormFieldProps } from 'fe-design-base/atoms/FormField';
import TextInput, { TextInputProps } from 'fe-design-base/atoms/TextInput';
import { Field, FormikValues, useFormikContext } from 'formik';

export type TextFieldProps = Omit<FormFieldProps, 'children'> &
  Omit<TextInputProps, 'value' | 'onChange'>;

const NumberAsStringField = ({
  name,
  label,
  inputId,
  width,
  fullWidth,
  labelPosition,
  labelWidth,
  shrink,
  disabled,
  helperText,
  hasAsterisk,
  successText,
  onBlur,
  ...inputProps
}: TextFieldProps) => {
  const { touched, errors, values, setFieldValue } =
    useFormikContext<FormikValues>();
  const error = touched?.[name] && errors?.[name] && !disabled;
  const fieldId = useId();
  const fieldValue = values[name];

  useEffect(() => {
    setFieldValue(name, fieldValue?.replace(/\D/g, ''));
  }, [fieldValue, name, setFieldValue]);

  const isRequired = hasAsterisk && !disabled && !inputProps.readOnly;

  return (
    <FormField
      label={label}
      name={name}
      inputId={inputId || fieldId}
      width={width}
      fullWidth={fullWidth}
      labelPosition={labelPosition}
      labelWidth={labelWidth}
      shrink={shrink}
      disabled={disabled}
      readOnly={inputProps.readOnly}
      uxElement={inputProps.uxElement}
      helperText={helperText}
      hasAsterisk={hasAsterisk}
      successText={successText}
    >
      <Field
        className="FDBNumberAsString"
        as={TextInput}
        name={name}
        id={inputId || fieldId}
        disabled={disabled}
        label={label}
        labelPosition={labelPosition}
        error={error}
        // Note: using onBlurCapture to avoid interfering with Formik handleBlur & validation
        onBlurCapture={onBlur}
        required={isRequired}
        inputMode="numeric"
        noTooltip
        {...inputProps}
      />
    </FormField>
  );
};

export default NumberAsStringField;
